import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Checkbox } from './ui/Checkbox';
import { Badge } from './ui/Badge';
import { OPEN_LOANS } from '../data/mockData';
import { PendingChange } from '../types/ownership';
import { CheckCircle2, AlertCircle } from 'lucide-react';
interface LoanSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedLoanIds: string[]) => void;
  pendingChanges: PendingChange[];
}
export function LoanSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  pendingChanges
}: LoanSelectionModalProps) {
  const [selectedLoanIds, setSelectedLoanIds] = useState<string[]>([]);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLoanIds(OPEN_LOANS.map(l => l.id));
    } else {
      setSelectedLoanIds([]);
    }
  };
  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedLoanIds(prev => [...prev, id]);
    } else {
      setSelectedLoanIds(prev => prev.filter(loanId => loanId !== id));
    }
  };
  const handleConfirm = () => {
    setIsSuccess(true);
    // Show success animation briefly before closing
    setTimeout(() => {
      onConfirm(selectedLoanIds);
      setIsSuccess(false);
      setSelectedLoanIds([]);
    }, 1500);
  };
  if (!isOpen) return null;
  const adds = pendingChanges.filter(c => c.type === 'add').length;
  const edits = pendingChanges.filter(c => c.type === 'edit').length;
  const deletes = pendingChanges.filter(c => c.type === 'delete').length;
  return <Modal isOpen={isOpen} onClose={onClose} title="Apply Ownership Changes" size="lg" footer={!isSuccess && <>
            <Button variant="neutral" onClick={onClose} data-id="element-4">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} disabled={selectedLoanIds.length === 0} data-id="element-5">
              Apply to {selectedLoanIds.length} Loan
              {selectedLoanIds.length !== 1 ? 's' : ''}
            </Button>
          </>} data-id="element-3">
      {isSuccess ? <div className="flex flex-col items-center justify-center py-12 animate-in fade-in zoom-in duration-300" data-id="element-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-7">
            <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-8" />
          </div>
          <h3 className="text-xl font-bold text-[#080707] mb-2" data-id="element-9">
            Changes Applied Successfully
          </h3>
          <p className="text-[#706e6b]" data-id="element-10">
            Updated ownership for {selectedLoanIds.length} loans.
          </p>
        </div> : <div className="space-y-6" data-id="element-11">
          {/* Summary */}
          <div className="bg-[#f3f2f2] p-4 rounded-[4px] border border-[#dddbda]" data-id="element-12">
            <h4 className="text-sm font-bold text-[#080707] mb-2 flex items-center gap-2" data-id="element-13">
              <AlertCircle className="w-4 h-4 text-[#0176d3]" data-id="element-14" />
              Pending Changes Summary
            </h4>
            <div className="flex gap-4 text-sm text-[#3e3e3c]" data-id="element-15">
              <div className="flex items-center gap-2" data-id="element-16">
                <span className="w-2 h-2 rounded-full bg-green-500" data-id="element-17"></span>
                <span data-id="element-18">{adds} New Records</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-19">
                <span className="w-2 h-2 rounded-full bg-amber-500" data-id="element-20"></span>
                <span data-id="element-21">{edits} Modified</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-22">
                <span className="w-2 h-2 rounded-full bg-red-500" data-id="element-23"></span>
                <span data-id="element-24">{deletes} Deleted</span>
              </div>
            </div>
            <p className="text-xs text-[#706e6b] mt-3" data-id="element-25">
              Select the loans below that should be updated with these ownership
              changes.
            </p>
          </div>

          {/* Loans Table */}
          <div className="border border-[#dddbda] rounded-[4px] overflow-hidden" data-id="element-26">
            <table className="w-full text-sm text-left" data-id="element-27">
              <thead className="bg-[#f3f2f2] text-[#3e3e3c] font-bold uppercase text-xs" data-id="element-28">
                <tr data-id="element-29">
                  <th className="px-4 py-3 border-b border-[#dddbda] w-12" data-id="element-30">
                    <Checkbox checked={selectedLoanIds.length === OPEN_LOANS.length && OPEN_LOANS.length > 0} onChange={e => handleSelectAll(e.target.checked)} data-id="element-31" />
                  </th>
                  <th className="px-4 py-3 border-b border-[#dddbda]" data-id="element-32">
                    Loan Number
                  </th>
                  <th className="px-4 py-3 border-b border-[#dddbda]" data-id="element-33">
                    Borrower
                  </th>
                  <th className="px-4 py-3 border-b border-[#dddbda]" data-id="element-34">
                    Amount
                  </th>
                  <th className="px-4 py-3 border-b border-[#dddbda]" data-id="element-35">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#dddbda]" data-id="element-36">
                {OPEN_LOANS.map(loan => <tr key={loan.id} className="hover:bg-[#f3f2f2] transition-colors" data-id="element-37">
                    <td className="px-4 py-3" data-id="element-38">
                      <Checkbox checked={selectedLoanIds.includes(loan.id)} onChange={e => handleSelectOne(loan.id, e.target.checked)} data-id="element-39" />
                    </td>
                    <td className="px-4 py-3 font-medium text-[#0176d3]" data-id="element-40">
                      {loan.loanNumber}
                    </td>
                    <td className="px-4 py-3" data-id="element-41">{loan.borrowerName}</td>
                    <td className="px-4 py-3 font-mono" data-id="element-42">
                      ${loan.loanAmount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3" data-id="element-43">
                      <Badge variant={loan.status === 'Active' ? 'success' : 'neutral'} data-id="element-44">
                        {loan.status}
                      </Badge>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </Modal>;
}